import React, { FC } from 'react';
import {IHistory} from "../types";
import moment from "moment/moment";

interface TimelineEvent {
    track_in: string;
    track_out: string;
}

interface TimelineProps {
    data: IHistory[] | undefined;
}

const Timeline: FC<TimelineProps> = ({ data }) => {
    // Helper functions to parse dates and format times
    const parseDate = (dateString: string): Date => new Date(dateString);

    const formatTime = (date: Date): string => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
        const formattedMinutes = minutes.toString().padStart(2, '0');
        return `${formattedHours}:00 ${ampm}`;
    };

    const formatDate = (date: Date): string => {
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    };

    // Get all dates from the data
    const allDates = data?.map(item => {
        const inDate = parseDate(item.track_in);
        return formatDate(inDate);
    }) || [];

    // Get unique dates - compatible with older TypeScript targets
    const uniqueDatesSet = new Set(allDates);
    const uniqueDates = Array.from(uniqueDatesSet);

    // Generate time slots with 2-hour intervals
    const generateTimeSlots = (): string[] => {
        const slots: string[] = [];
        for (let hour = 0; hour <= 24; hour += 2) { // Use 2-hour intervals
            const displayHour = hour % 24; // Handle the final hours
            slots.push(`${displayHour % 12 === 0 ? 12 : displayHour % 12}:00 ${displayHour < 12 ? 'AM' : 'PM'}`);
        }
        return slots;
    };

    const timeSlots = generateTimeSlots();

    // Calculate position and width for each event
    const calculateEventStyle = (item: IHistory): React.CSSProperties => {
        const inDate = parseDate(item.track_in);
        let outDate = new Date();
        if(item.track_out){
            outDate = parseDate(item.track_out);
        }else{
            outDate = parseDate("");
        }


        const inHour = inDate.getHours() + inDate.getMinutes() / 60;
        const outHour = outDate.getHours() + outDate.getMinutes() / 60;

        const startPercentage = (inHour / 24) * 100;
        const duration = outHour - inHour;
        const widthPercentage = (duration / 24) * 100;

        return {
            left: `${startPercentage}%`,
            width: `${widthPercentage}%`,
            backgroundColor: '#FFA500',
            height: '100%',
            position: 'absolute',
            borderRadius: '2px'
        };
    };

    // Sort dates chronologically
    const sortedDates = [...uniqueDates].sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA.getTime() - dateB.getTime();
    });

    return (
        <div className="timeline-container" style={{ width: '100%', overflowX: 'auto' }}>
            <div style={{
                display: 'flex',
                borderBottom: '1px solid #e5e7eb',
                marginLeft: '100px',
                position: 'relative',
                height: '30px'
            }}>
                {timeSlots.map((slot, index) => (
                    <div
                        key={index}
                        style={{
                            width: `${100 / (timeSlots.length - 1)}%`,
                            textAlign: 'center',
                            position: 'absolute',
                            left: `${(index / (timeSlots.length - 1)) * 100}%`,
                            fontSize: '12px',
                            borderLeft: index > 0 ? '1px solid #e5e7eb' : 'none'
                        }}
                    >
                        {slot}
                    </div>
                ))}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <tbody>
                {sortedDates.map((date, dateIndex) => (
                    <tr key={dateIndex} style={{ height: '40px', borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{
                            width: '100px',
                            padding: '0 8px',
                            borderRight: '1px solid #e5e7eb',
                            textAlign: 'left',
                            fontWeight: 'normal',
                            fontSize: '14px'
                        }}>
                            {moment(date).format("MMM-DD-YYYY")}
                        </td>
                        <td style={{ position: 'relative', padding: 0 }}>
                            <div style={{ display: 'flex', position: 'absolute', width: '100%', height: '24px', top: '8px' }}>
                                {data?.filter(item => formatDate(parseDate(item.track_in)) === date)
                                    .map((item, itemIndex) => (
                                        <div
                                            key={itemIndex}
                                            style={calculateEventStyle(item)}
                                        />
                                    ))}
                            </div>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default Timeline;